import React, { useEffect, useState } from 'react';
import { Input } from 'antd';
import {
  CODING_LANGUAGES, DEFAULT_STARTER_CODE, DEFAULT_MEMORY_LIMIT_MB, DEFAULT_TIME_LIMIT_MS,
  languageLabel, newTestCase, emptyCodingContent,
} from './codingLanguages';
import './codingQuestion.css';

const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];

// Monospace editor with line numbers; Tab inserts spaces
const CodeArea = ({ value, onChange, minRows = 8, readOnly }) => {
  const lines = Math.max((value || '').split('\n').length, minRows);
  const onKeyDown = (e) => {
    if (e.key !== 'Tab' || readOnly) return;
    e.preventDefault();
    const el = e.target;
    const { selectionStart: s, selectionEnd: t } = el;
    const next = `${value.slice(0, s)}    ${value.slice(t)}`;
    onChange(next);
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 4; });
  };
  return (
    <div className='cq-code'>
      <div className='cq-code-gutter'>
        {Array.from({ length: lines }, (_, i) => <div key={i}>{i + 1}</div>)}
      </div>
      <textarea
        className='cq-code-text'
        spellCheck={false}
        rows={lines}
        value={value || ''}
        readOnly={readOnly}
        onKeyDown={onKeyDown}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

const Label = ({ children, right }) => (
  <div className='cq-label-row'>
    <label className='cq-label'>{children}</label>
    {right ? <span className='cq-label-hint'>{right}</span> : null}
  </div>
);

const TestCaseRow = ({ testCase, index, expanded, onToggle, onChange, onRemove, canRemove, readOnly }) => {
  const set = (key, value) => onChange({ ...testCase, [key]: value });
  return (
    <div className='cq-tc'>
      <div className={`cq-tc-head ${expanded ? 'cq-tc-head-open' : ''}`} onClick={onToggle}>
        <span className='cq-tc-index'>{index + 1}</span>
        <button
          type='button'
          className={`cq-badge ${testCase.hidden ? 'cq-badge-hidden' : 'cq-badge-sample'}`}
          title={readOnly ? undefined : 'Sample tests are shown to students; hidden tests are not'}
          onClick={(e) => { e.stopPropagation(); if (!readOnly) set('hidden', !testCase.hidden); }}
        >
          {testCase.hidden ? 'HIDDEN' : 'SAMPLE'}
        </button>
        <Input
          size='small'
          className='cq-tc-name'
          placeholder={`Test case ${index + 1}`}
          value={testCase.name}
          disabled={readOnly}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => set('name', e.target.value)}
        />
        <span className='cq-tc-meta' onClick={(e) => e.stopPropagation()}>
          weight
          <input
            type='number' min={1} className='cq-num' disabled={readOnly}
            value={testCase.weight}
            onChange={(e) => set('weight', Math.max(1, parseInt(e.target.value, 10) || 1))}
          />×
        </span>
        <span className='cq-tc-meta' onClick={(e) => e.stopPropagation()}>
          <input
            type='number' min={100} step={100} className='cq-num cq-num-wide' disabled={readOnly}
            value={testCase.timeLimitMs}
            onChange={(e) => set('timeLimitMs', Math.max(100, parseInt(e.target.value, 10) || DEFAULT_TIME_LIMIT_MS))}
          />ms
        </span>
        {canRemove && !readOnly
          ? <button type='button' className='cq-x' title='Remove test case' onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>
          : <span className='cq-x-placeholder' />}
      </div>
      {expanded ? (
        <div className='cq-tc-body'>
          <div>
            <div className='cq-sublabel'>Input</div>
            <textarea
              className='cq-mono-area' rows={4} spellCheck={false} disabled={readOnly}
              placeholder='What the program reads from stdin'
              value={testCase.input}
              onChange={(e) => set('input', e.target.value)}
            />
          </div>
          <div>
            <div className='cq-sublabel'>Expected output <span className='cq-req'>*</span></div>
            <textarea
              className='cq-mono-area' rows={4} spellCheck={false} disabled={readOnly}
              placeholder='What the program must print'
              value={testCase.expectedOutput}
              onChange={(e) => set('expectedOutput', e.target.value)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CodingQuestionFields = ({ questionContent, answerContent, updateCodingQuestion, readOnly }) => {
  const isSeeded = Array.isArray(questionContent?.languages) && Array.isArray(answerContent?.testCases);

  // New question (or one cleared after save): fill in the defaults, keep any statement typed
  useEffect(() => {
    if (!isSeeded && !readOnly) {
      const seed = emptyCodingContent(questionContent?.statement ?? null);
      updateCodingQuestion(
        { ...seed.questionContent, ...(questionContent || {}), languages: seed.questionContent.languages, starterCode: seed.questionContent.starterCode },
        seed.answerContent,
      );
    }
  }, [isSeeded]);

  const qc = questionContent || {};
  const ac = answerContent || {};
  const languages = qc.languages || [];
  const starterCode = qc.starterCode || {};
  const testCases = ac.testCases || [];

  const [activeLang, setActiveLang] = useState(languages[0]);
  const [openCase, setOpenCase] = useState(testCases[0]?.id);

  useEffect(() => {
    if (!languages.includes(activeLang)) setActiveLang(languages[0]);
  }, [languages.join(',')]);
  useEffect(() => {
    if (testCases.length && !testCases.find(t => t.id === openCase)) setOpenCase(testCases[0].id);
  }, [testCases.map(t => t.id).join(',')]);

  if (!isSeeded) return null;

  const setQC = (patch) => updateCodingQuestion({ ...qc, ...patch }, ac);
  const setAC = (patch) => updateCodingQuestion(qc, { ...ac, ...patch });

  const toggleLanguage = (id) => {
    if (readOnly) return;
    if (languages.includes(id)) {
      if (languages.length === 1) return;
      setQC({ languages: languages.filter(l => l !== id) });
    } else {
      const ordered = CODING_LANGUAGES.map(l => l.id).filter(l => l === id || languages.includes(l));
      setQC({ languages: ordered, starterCode: { [id]: DEFAULT_STARTER_CODE[id], ...starterCode } });
      setActiveLang(id);
    }
  };

  const updateCase = (i, tc) => setAC({ testCases: testCases.map((t, j) => (j === i ? tc : t)) });
  const removeCase = (i) => setAC({ testCases: testCases.filter((_, j) => j !== i) });
  const addCase = () => {
    const tc = newTestCase(testCases.length > 0);
    setAC({ testCases: [...testCases, tc] });
    setOpenCase(tc.id);
  };

  const sampleCount = testCases.filter(t => !t.hidden).length;
  const hiddenCount = testCases.length - sampleCount;
  const copySource = languages.find(l => l !== activeLang && starterCode[l]);

  return (
    <div className='cq-root'>
      <div className='cq-grid-2'>
        <div>
          <Label>Title</Label>
          <Input
            className='cq-input' placeholder='e.g. Sum of two numbers' disabled={readOnly}
            value={qc.title} onChange={(e) => setQC({ title: e.target.value })}
          />
        </div>
        <div>
          <Label>Difficulty</Label>
          <div className='cq-segment'>
            {DIFFICULTIES.map(d => (
              <button
                key={d} type='button' disabled={readOnly}
                className={`cq-segment-btn ${qc.difficulty === d ? 'cq-segment-on' : ''}`}
                onClick={() => setQC({ difficulty: d })}
              >{d}</button>
            ))}
          </div>
        </div>
      </div>

      <div className='cq-grid-3'>
        {[['constraints', 'Constraints', '1 ≤ N ≤ 10^5'], ['inputFormat', 'Input format', 'First line: N\nNext line: N integers'], ['outputFormat', 'Output format', 'Print the sum']].map(([key, title, ph]) => (
          <div key={key}>
            <Label>{title}</Label>
            <textarea
              className='cq-mono-area' rows={4} spellCheck={false} disabled={readOnly}
              placeholder={ph} value={qc[key] || ''}
              onChange={(e) => setQC({ [key]: e.target.value })}
            />
          </div>
        ))}
      </div>

      <div>
        <Label right={`${languages.length} of ${CODING_LANGUAGES.length} selected`}>Allowed languages &amp; starter code</Label>
        <div className='cq-chips'>
          {CODING_LANGUAGES.map(l => {
            const on = languages.includes(l.id);
            return (
              <button
                key={l.id} type='button' disabled={readOnly}
                className={`cq-chip ${on ? 'cq-chip-on' : ''}`}
                title={on && languages.length === 1 ? 'At least one language is needed' : undefined}
                onClick={() => toggleLanguage(l.id)}
              >{on ? '✓' : '+'} {l.label}</button>
            );
          })}
        </div>
        <div className='cq-editor'>
          <div className='cq-editor-bar'>
            <div className='cq-tabs'>
              {languages.map(id => (
                <div
                  key={id}
                  className={`cq-tab ${id === activeLang ? 'cq-tab-on' : ''}`}
                  onClick={() => setActiveLang(id)}
                >{languageLabel(id)}</div>
              ))}
            </div>
            <div className='cq-flex' />
            {copySource && !readOnly ? (
              <button
                type='button' className='cq-small-btn'
                onClick={() => setQC({ starterCode: { ...starterCode, [activeLang]: starterCode[copySource] } })}
              >Copy from {languageLabel(copySource)}</button>
            ) : null}
            {!readOnly ? (
              <button
                type='button' className='cq-small-btn'
                onClick={() => setQC({ starterCode: { ...starterCode, [activeLang]: DEFAULT_STARTER_CODE[activeLang] } })}
              >Reset</button>
            ) : null}
          </div>
          <CodeArea
            readOnly={readOnly}
            value={starterCode[activeLang] || ''}
            onChange={(code) => setQC({ starterCode: { ...starterCode, [activeLang]: code } })}
          />
        </div>
        <div className='cq-hint'>Students start from this code. Programs read from standard input and print to standard output.</div>
      </div>

      <div>
        <div className='cq-label-row'>
          <label className='cq-label'>Test cases · {sampleCount} sample · {hiddenCount} hidden</label>
          {!readOnly ? <button type='button' className='cq-primary-btn' onClick={addCase}>+ Add test case</button> : null}
        </div>
        <div className='cq-tc-list'>
          {testCases.map((tc, i) => (
            <TestCaseRow
              key={tc.id}
              testCase={tc}
              index={i}
              readOnly={readOnly}
              expanded={openCase === tc.id}
              onToggle={() => setOpenCase(openCase === tc.id ? null : tc.id)}
              onChange={(next) => updateCase(i, next)}
              onRemove={() => removeCase(i)}
              canRemove={testCases.length > 1}
            />
          ))}
        </div>
        <div className='cq-hint'>Sample tests are shown to students in the question. Hidden tests are only used for checking answers.</div>
      </div>

      <div className='cq-limits'>
        <div className='cq-limits-title'>Execution limits</div>
        <div className='cq-limits-row'>
          <span>Memory</span>
          <span>
            <input
              type='number' min={16} step={16} className='cq-num cq-num-wide' disabled={readOnly}
              value={ac.memoryLimitMb ?? DEFAULT_MEMORY_LIMIT_MB}
              onChange={(e) => setAC({ memoryLimitMb: Math.max(16, parseInt(e.target.value, 10) || DEFAULT_MEMORY_LIMIT_MB) })}
            /> MB
          </span>
        </div>
        <div className='cq-limits-row'><span>Time</span><span className='cq-muted'>set on each test case</span></div>
        <div className='cq-limits-row'><span>Network</span><span className='cq-danger'>Disabled</span></div>
      </div>
    </div>
  );
};

// Problems that block saving, in the order the teacher would fix them
export const codingQuestionErrors = (questionContent, answerContent) => {
  const qc = questionContent || {};
  const testCases = answerContent?.testCases || [];
  if (!qc.title || !qc.title.trim()) return 'Please add a title';
  if (!qc.languages?.length) return 'Please allow at least one language';
  if (!testCases.length) return 'Please add at least one test case';
  if (!testCases.some(t => !t.hidden)) return 'Please keep at least one sample test case';
  const blank = testCases.findIndex(t => !String(t.expectedOutput || '').trim());
  if (blank >= 0) return `Please add the expected output for test case ${blank + 1}`;
  return null;
};

export default CodingQuestionFields;
